import React, { useCallback, useEffect, useState, useMemo } from 'react'
import AttachImageToTree from '@fs/zion-attach-image-to-tree'
import { InfoSheet, useStatusOverlay, InfoSheetPanel, usePageWidth } from '@fs/zion-ui'
import { usePermission } from '@fs/zion-permissions'
import { useQueryParams, useLocation } from '@fs/zion-router'
import zionDebug from '@fs/zion-debug'
import { baseUrl } from '@fs/zion-config'
import { trackEvent } from '@fs/zion-analytics'
import { splunk } from '@fs/zion-splunk-logger'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useGroupData, useImageOrganizeModes, useErrorStatus, useDetermineMode } from '../../views/imageBrowse/hooks'
import { CheckboxProvider } from '../../views/imageBrowse/providers'
import { getTitleFromGroup } from '../../views/imageBrowse/helpers'
import ViewMetadata from '../../../shared/components/viewMetadata/ViewMetadata'
import transl from './SideSheet.transl'
import EditGroupData from '../../views/imageBrowse/groupData/EditGroupData'
import useInfoSheetOrientationCachedState from '../../../shared/hooks/useInfoSheetOrientationCachedState'
import SideSheetBody from './SideSheetBody'
import SideSheetFooter from './SideSheetFooter'

const debug = zionDebug('siv:LightyearSideSheet')

const SideSheet = () => {
  const { infoSheetOverlay, editMetadataOverlay, openOverlay, attachToTreeOverlay } = useOverlayContext()
  const { itemNumber, itemCount, group } = useGroupData()
  const { errorStatus, isGroupError } = useErrorStatus()
  const { showMetadataAudit, showImageQualityAudit } = useDetermineMode()
  const [isAuditProjectsAuthorized, auditProjectsAuthorizedStatus] = usePermission(
    'LightyearAuditProjectsPermission',
    'LightyearContext'
  )
  const [pageLoaded, setPageLoaded] = useState(false)
  const pageWidth = usePageWidth()
  const isNarrowViewport = pageWidth({ default: true, sm: false })
  const { isBottomSheet, setInfoSheetOrientation } = useInfoSheetOrientationCachedState(isNarrowViewport)
  const location = useLocation()
  const imageArk = location.pathname.split('/').pop()

  const attachmentUrls = useMemo(() => {
    return [`${baseUrl}/ark:/61903/${imageArk}`]
  }, [imageArk])

  const showStatusOverlay = useStatusOverlay()
  const { query, setQuery } = useQueryParams({ replaceState: true })
  const { mode } = query

  const handleUnauthorizedImageOrganize = useCallback(() => {
    showStatusOverlay({
      message: transl.doNotHavePermission(),
      dismissable: true,
    })
  }, [showStatusOverlay])

  const { isImageOrganizeMode, isDivideMode, isMetadataAllMode, isMetadataSingleMode } = useImageOrganizeModes({
    unauthorizedCallback: handleUnauthorizedImageOrganize,
  })

  // Get the current view title based on query and mode
  const getCurrentViewTitle = () => {
    if (showMetadataAudit) return null

    // Handle DIVIDE mode first
    if (isDivideMode) return transl.divideImageGroup()

    switch (query.view) {
      case 'explore':
        return transl.information()
      case 'organize':
        if (isMetadataAllMode || isMetadataSingleMode) return transl.correctMetadata()
        return transl.information()
      default:
        return transl.information()
    }
  }

  useEffect(() => {
    if (
      (mode === 'imageQualityAudit' || mode === 'metadataAudit') &&
      !isAuditProjectsAuthorized &&
      auditProjectsAuthorizedStatus === 'loaded'
    ) {
      setQuery({ mode: undefined })
      showStatusOverlay({
        message: transl.unauthorizedAudit(),
        dismissable: true,
      })
    }
  }, [isAuditProjectsAuthorized, mode, setQuery, auditProjectsAuthorizedStatus, showStatusOverlay])

  const editPanelTitle = useMemo(() => {
    if (itemCount < 2) return transl.editImageGroup()
    if (isImageOrganizeMode) {
      if (isDivideMode) return transl.divideImageGroup()
      if (isMetadataAllMode || isMetadataSingleMode) return transl.correctMetadata()
    }
    return transl.editItem(itemNumber || '')
  }, [itemCount, itemNumber, isDivideMode, isImageOrganizeMode, isMetadataAllMode, isMetadataSingleMode])

  useEffect(() => {
    let isMounted = true
    const lastQueryInStorage = localStorage.getItem('ly.images.searchResults.lastQuery') || '{}'
    const { hasMissingCoverageData } = JSON.parse(lastQueryInStorage)
    const currentWorkFlowActions = group?.currentWorkflowActions || []
    const groupHasMissingCoverageData = currentWorkFlowActions.find(
      (wfa) => wfa.name === 'AuditMissingCoverage' && wfa.status === 'Available'
    )

    if (hasMissingCoverageData && groupHasMissingCoverageData && !pageLoaded && isMounted) {
      setPageLoaded(true)
    }

    return () => {
      isMounted = false
    }
  }, [group, pageLoaded])

  useEffect(() => {
    if (showMetadataAudit) openOverlay('editMetadataOverlay')
  }, [showMetadataAudit, openOverlay])

  debug({
    isImageOrganizeMode,
    isDivideMode,
    isMetadataAllMode,
    isMetadataSingleMode,
    group,
    imageArk,
    attachmentUrls,
  })

  return !(errorStatus && isGroupError) ? (
    <InfoSheet
      nonClosable={showMetadataAudit || isDivideMode}
      dockable
      size="md"
      title={!showMetadataAudit && getCurrentViewTitle()}
      {...infoSheetOverlay}
      isBottomSheet={isBottomSheet}
      setIsBottomSheet={setInfoSheetOrientation}
      footer={showImageQualityAudit ? <SideSheetFooter showImageQualityAudit={showImageQualityAudit} /> : null}
    >
      <SideSheetBody />
      <InfoSheetPanel {...editMetadataOverlay} title={editPanelTitle}>
        <CheckboxProvider>
          <EditGroupData />
        </CheckboxProvider>
      </InfoSheetPanel>
      {group && JSON.stringify(group) !== '{}' && (
        <AttachImageToTree
          {...{
            imageURL: window.location,
            key: group.naturalGroupId,
            imageTitle: getTitleFromGroup(group),
            attachToTreeFirstPanel: attachToTreeOverlay,
            additionalContentStep1: <ViewMetadata />,
            attachmentUrls,
            onAttachImageToTree: (response) => {
              const attachedPersonId = response?.responses?.[0]?.person?.id
              trackEvent({
                link_name: 'attach-to-tree-completed',
                event_name: 'collection_image_attach',
                attachees: response?.responses?.length,
              })
              splunk.log({
                message: 'Attach to Tree completed',
                imageArk,
                personId: attachedPersonId,
              })
            },
          }}
        />
      )}
    </InfoSheet>
  ) : null
}

export default SideSheet
